import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/shared/ui';
import { useFeature } from '@/entities/session/auth-store';

/**
 * Feature gating for the web shells (ADR-0076). The API's `FeatureGuard` already enforces every
 * disabled module with a 403 — but a 403 that reaches a mounted page bubbles to the error boundary
 * ("Something went sideways"). So we ALSO hide disabled features in the client: nav items drop out
 * (see AppShell/AdminShell) and routed pages render a calm "not enabled" state instead of mounting,
 * fetching, and erroring. `useFeature()` reads the household's enabled optional-feature set from `me`.
 */

/** Friendly "this isn't part of your family's setup" surface for a disabled-feature route. */
export function FeatureDisabled(): React.ReactElement {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      art="empty.locked"
      glyph="🔒"
      message={t('features.disabledTitle')}
      description={t('features.disabledBody')}
    />
  );
}

/**
 * Wrap a routed page component so it renders {@link FeatureDisabled} when the household has the given
 * optional feature turned off — the deep-link / bookmark backstop behind the hidden nav entry. The
 * page never mounts (so it never fires its feature-gated requests) when the feature is off.
 */
// eslint-disable-next-line react-refresh/only-export-components -- HOC factory, not a component export
export function withFeature(feature: string, Component: React.ComponentType): React.ComponentType {
  function FeatureGated(): React.ReactElement {
    const hasFeature = useFeature();
    return hasFeature(feature) ? <Component /> : <FeatureDisabled />;
  }
  FeatureGated.displayName = `withFeature(${feature})`;
  return FeatureGated;
}
